<template>
    <SignaloDialog
        :icon="'mdi-cog-outline'"
        :open="open"
        @close="() => onClose(false)"
        :title="item?.name ? item?.name : $t('common.addNewMachine')"
    >
        <template #body>
            <SignaloPageLoading v-if="loading" />
            <div v-else>
                <v-tabs v-model="tabs" color="primary">
                    <v-tab :value="data"> {{ $t('common.data') }} </v-tab>
                    <v-tab :value="parameters">
                        {{ $t('common.parameters') }}
                        <span
                            v-if="
                                equipmentParameters.filter((ep) => ep.required)
                                    .length
                            "
                            class="required-field-label"
                        ></span>
                    </v-tab>
                    <v-tab :value="costs" v-if="mode === 'edit'">
                        {{ $t('common.costs') }}
                    </v-tab>
                    <v-tab :value="photos" v-if="mode === 'edit'">
                        {{ $t('common.images') }}
                    </v-tab>
                    <v-tab :value="documents" v-if="mode === 'edit'">
                        {{ $t('common.documents') }}
                    </v-tab>
                    <v-tab :value="checklist" v-if="mode === 'edit'">
                        {{ $t('common.checkList') }}
                    </v-tab>
                </v-tabs>
                <v-window v-model="tabs">
                    <v-window-item key="data">
                        <v-card-text>
                            <v-form
                                ref="dataForm"
                                v-model="dataValid"
                                lazy-validation
                            >
                                <v-row>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.name')"
                                            v-model="item.name"
                                            :max="4"
                                            :rules="nameRules"
                                            required
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            class="required-field"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.EQUIPMENT_TYPE
                                            "
                                            :label="$t('common.type')"
                                            v-model="item.equipment_type_id"
                                            required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="$t('common.status')"
                                            placeholder="Status"
                                            :modelValue="
                                                statuses.find(
                                                    (x) =>
                                                        x.keyname ==
                                                        item.status_keyname
                                                )
                                            "
                                            @update:modelValue="
                                                (x) => (item.status_keyname = x)
                                            "
                                            :items="statuses"
                                            item-title="value"
                                            item-value="keyname"
                                            clearable
                                            @click:clear="
                                                item.status_keyname = null
                                            "
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            clear-icon="mdi-close-circle-outline"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.designation')"
                                            v-model="item.designation"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.symbol')"
                                            v-model="item.symbol"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.serialNumber')"
                                            v-model="item.serial_no"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="
                                                $t(
                                                    'tableHeaders.registryNumber'
                                                )
                                            "
                                            v-model="item.registry_no"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.udtNumber')"
                                            v-model="item.superv_office_no"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="
                                                $t('common.manufacturedYear')
                                            "
                                            v-model="item.manufactured_year"
                                            :rules="yearRules"
                                            :counter="4"
                                            :min="1900"
                                            type="number"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.LOCATION
                                            "
                                            v-model="item.location_id"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.COMPANY
                                            "
                                            :label="$t('common.manufacturer')"
                                            v-model="item.manufacturer_id"
                                            hide-details
                                            clearable
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.COMPANY
                                            "
                                            :label="$t('common.supplier')"
                                            v-model="item.supplier_id"
                                            clearable
                                        />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.DICTIONARY
                                            "
                                            :dictionary="'prod_lines'"
                                            :label="$t('common.productionLine')"
                                            v-model="item.prod_line"
                                            clearable
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="$t('common.currency')"
                                            v-model="item.currency_id"
                                            :items="currencies"
                                            :item-title="`name[${$i18n.locale}]`"
                                            item-value="id"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            clear-icon="mdi-close-circle-outline"
                                            class="required-field"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="
                                                $t('common.statisticStrategy')
                                            "
                                            v-model="item.statistics_strategy"
                                            :items="statisticStrategies"
                                            :item-title="
                                                (item) =>
                                                    $t(
                                                        `common.${item.strategy_type}`
                                                    )
                                            "
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            clear-icon="mdi-close-circle-outline"
                                            clearable
                                            return-object
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="
                                                $t('tableHeaders.criticality')
                                            "
                                            v-model="item.criticality"
                                            :items="eq_criticality"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            clear-icon="mdi-close-circle-outline"
                                            clearable
                                            item-title="value"
                                            item-value="keyname"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.DICTIONARY
                                            "
                                            :dictionary="'departments'"
                                            :label="
                                                $t('tableHeaders.department')
                                            "
                                            v-model="item.department"
                                            clearable
                                        />
                                    </v-col>
                                    <v-col
                                        cols="12"
                                        md="4"
                                        xl="3"
                                        v-if="
                                            authStore.isAllowed(
                                                'products',
                                                'read'
                                            )
                                        "
                                    >
                                        <SignaloNewValueSelect
                                            :selectableType="
                                                SELECTABLE_TYPES.PRODUCT
                                            "
                                            v-model="item.current_product_id"
                                            clearable
                                            :disabled="
                                                !authStore.isAllowed(
                                                    'equipment.product',
                                                    'update'
                                                )
                                            "
                                        />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" md="12" xl="12">
                                        <SignaloBarCode
                                            ref="barcodeComponent"
                                            :barcode="item?.barcode"
                                            :editable="true"
                                            @onChange="onBarcodeChangeHandler"
                                        />
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="parameters" :eager="true">
                        <SignaloItemParametersManage
                            :equipmentId="this.editedItem?.id"
                            :equipmentTypeId="
                                this.editedItem?.equipment_type_id ||
                                this.item?.equipment_type_id
                            "
                            @everyRequiredParamHasValue="
                                onEveryRequiredParamHasValue
                            "
                        />
                    </v-window-item>
                    <v-window-item key="costs" v-if="mode === 'edit'">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="4" xl="3">
                                    <SignaloNewValueSelect
                                        :selectableType="
                                            SELECTABLE_TYPES.COST_CENTER
                                        "
                                        :label="$t('costCenter.costCenter')"
                                        v-model="item.cost_center_id"
                                        clearable
                                    />
                                </v-col>
                                <v-col
                                    cols="12"
                                    md="4"
                                    xl="3"
                                    v-if="
                                        authStore.isAllowed(
                                            'financial_data',
                                            'create'
                                        )
                                    "
                                >
                                    <v-text-field
                                        :label="$t('common.hourlyCost')"
                                        v-model="item.work_hour_cost"
                                        clearable
                                        @click:clear="
                                            item.work_hour_cost = null
                                        "
                                        variant="outlined"
                                        color="var(--greyscale-100)"
                                        clear-icon="mdi-close-circle-outline"
                                        type="number"
                                        step="0.01"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="photos" v-if="mode === 'edit'">
                        <v-card-text>
                            <SignaloFilesImagesContainer
                                v-if="item?.id"
                                :endpoint="`/equipment/${item.id}/photos`"
                                allow-upload
                                allow-remove
                            />
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="documents" v-if="mode === 'edit'">
                        <v-card-text>
                            <SignaloFilesDocumentsContainer
                                v-if="item?.id"
                                :endpoint="`/equipment/${item.id}/documents`"
                                allow-upload
                                allow-remove
                            />
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="checklist" v-if="mode === 'edit'">
                        <v-card-text>
                            <v-textarea
                                v-model="item.checklist"
                                variant="outlined"
                            ></v-textarea>
                        </v-card-text>
                    </v-window-item>
                </v-window>
            </div>
        </template>
        <template #buttons>
            <v-btn @click="() => onClose(false)">
                {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
                :loading="dataSaving"
                color="primary"
                variant="elevated"
                @click="saveData"
                :disabled="
                    (equipmentParameters?.length
                        ? !everyRequiredParamHasValue
                        : false) || !isBarcodeValid
                "
            >
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </SignaloDialog>
</template>

<script>
import * as DATA_STORE from 'signalo-vue-data-store';
import * as SIGNALO_VUE_HELPERS_STORE from 'signalo-vue-helpers-store';
import * as AUTH_STORE from 'signalo-vue-auth-store';

export default {
    setup() {
        const dataStore = DATA_STORE.default();
        const helpersStore = SIGNALO_VUE_HELPERS_STORE.default();
        const authStore = AUTH_STORE.default();
        return { dataStore, helpersStore, authStore };
    },
    props: ['open', 'editedItem', 'mode'],
    data() {
        return {
            id: this.editedItem?.id,

            item: null,
            loading: true,

            tabs: 'data',

            // DATA
            dataValid: true,
            dataSaving: false,
            currencies: [],
            everyRequiredParamHasValue: this.mode === 'edit',
            equipmentParameters: [],
            companies: [],
            equipmentTypes: [],
            barcode: null,
            statisticStrategies: [],
        };
    },
    computed: {
        locations() {
            return this.dataStore.locations.values;
        },
        statuses() {
            return Object.values(this.dataStore.dictionary.group('eq_status'));
        },
        attributeTypes() {
            return [
                { value: 'text', text: this.$t('attributeTypes.text') },
                { value: 'note', text: this.$t('attributeTypes.note') },
                { value: 'integer', text: this.$t('attributeTypes.integer') },
                { value: 'decimal', text: this.$t('attributeTypes.decimal') },
                { value: 'date', text: this.$t('attributeTypes.date') },
                { value: 'time', text: this.$t('attributeTypes.time') },
                { value: 'datetime', text: this.$t('attributeTypes.dateTime') },
                { value: 'color', text: this.$t('attributeTypes.color') },
                { value: 'icon', text: this.$t('attributeTypes.icon') },
                { value: 'boolean', text: this.$t('attributeTypes.boolean') },
            ];
        },
        prod_lines() {
            return this.dataStore.dictionary.group('prod_lines');
        },
        nameRules() {
            return [(v) => !!v || this.$t('common.nameRequired')];
        },
        yearRules() {
            return [
                (v) => {
                    if (v === null) return true;
                    else if (v === undefined) return true;
                    else if (isNaN(v) || v <= 1900) {
                        return (
                            this.$t('errors.yearMustBeGreaterThan') + '1900.'
                        );
                    }
                    return true;
                },
            ];
        },
        isBarcodeValid() {
            if (this.barcode?.value) {
                return this.barcode.valid;
            }
            return true;
        },
        eq_criticality() {
            return this.dataStore.dictionary.group('eq_criticality');
        },
    },
    mounted() {
        this.loading = true;

        const dataToLoad = [
            this.dataStore.dictionary.promise(),
            this.dataStore.locations.promise(),
            this.axios.get('companies').then((data) => {
                this.companies = data?.data?.items || [];
            }),
            this.axios.get('equipment_types?type=machine').then((data) => {
                this.equipmentTypes = data?.data?.items || [];
            }),
            this.axios.get('global/currencies').then((res) => {
                this.currencies = res?.data?.items;
            }),
            this.axios
                .get('statistics_strategies?object_type_equals=Equipment')
                .then((res) => {
                    this.statisticStrategies = res?.data?.items || [];
                }),
        ];

        if (this.mode === 'edit') {
            dataToLoad.unshift(
                this.axios.get('/equipment/' + this.editedItem.id)
            );
        }

        Promise.allSettled(dataToLoad).then((promises) => {
            if (this.mode === 'edit' && promises[0].status === 'fulfilled') {
                let x = promises[0].value;

                if (x.data.status == 'ok') {
                    this.item = x.data.item;
                }
            } else {
                this.item = {
                    name: '',
                    currency_id: this.authStore?.tenant?.default_currency_id,
                };
            }

            this.loading = false;
        });
    },
    methods: {
        async saveData() {
            if (this.barcode?.valid) {
                this.item.barcode = this.barcode;
            }
            await this.$refs.dataForm.validate();
            if (!this.dataValid) return;
            let method;
            this.mode === 'edit' ? (method = 'PUT') : (method = 'POST');
            let url = '/equipment';
            if (this.mode === 'edit') {
                url += '/' + this.id;
            }
            this.item.type = 'machine';
            let newMachine;
            if (this.item.statistics_strategy) {
                this.item.statistics_strategy_id =
                    this.item.statistics_strategy.id;
                delete this.item.statistics_strategy;
            } else {
                this.item.statistics_strategy_id = null;
            }
            if (!this.authStore.isAllowed('equipment.product')) {
                delete this.item.current_product_id;
            }

            this.dataSaving = true;
            this.axios
                .request({
                    url,
                    method,
                    data: this.item,
                })
                .then((createdEquipment) => {
                    newMachine = createdEquipment.data.item;
                    return this.saveParams(
                        createdEquipment.data.item.id,
                        this.equipmentParameters.filter(
                            (p) => p.value !== undefined && p.value !== null
                        )
                    );
                })
                .then((/*{ data }*/) => {
                    this.dataSaving = false;
                    this.helpersStore.snackbar(
                        this.$t('common.saved'),
                        'success'
                    );
                    this.onClose(true, newMachine);
                })
                .catch((e) => {
                    if (e?.response?.status == 401) return;
                })
                .finally(() => {
                    this.dataSaving = false;
                });
        },
        onClose(refetchData = false, newMachine = null) {
            this.$emit('closeDialog', refetchData, newMachine);
            this.$forceUpdate();
        },
        onEveryRequiredParamHasValue(everyHasValue, parameters) {
            this.everyRequiredParamHasValue = everyHasValue;
            this.equipmentParameters = parameters;
        },
        saveParams(equipmentId, params) {
            const requests = [];
            params.forEach((p) => {
                let url = `equipment/${equipmentId}/parameters`;
                requests.push(
                    this.axios.post(url, {
                        parameter_id: p.id,
                        value: p.value,
                    })
                );
            });

            return Promise.all(requests);
        },
        onBarcodeChangeHandler(val) {
            this.barcode = val;
        },
    },
};
</script>

<style>
.eq-photo {
    position: relative;
    margin: 10px;
    width: 150px;
    height: 150px;
    border: 1px solid gray;
    border-radius: 10px;
    cursor: pointer;
    z-index: 101;
    overflow: hidden;
}

.eq-photo .cover {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
}

.eq-photo .cover:hover {
    background: rgba(200, 200, 200, 0.6);
}
</style>
